import React, { useEffect, useState, useCallback, useRef } from 'react'
import SwiperCore, { Navigation, A11y, EffectFade, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useEventListener } from 'react-recipes'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/effect-fade'

import arrowSlider from '../icons/arrow-slider.svg'

/* eslint-disable import/first */
SwiperCore.use([Navigation, A11y, EffectFade, Pagination])

const Slider = ({ options, children, name, hasCards = false, className = null, hasPagination = false, navCentered = false, nbItems = 0, paginationClass = false }) => {
  options = JSON.parse(options)

  const [hasNav, setHasNav] = useState(false)

  // Swiper must not be server-rendered/hydrated: on a production SSR build the
  // hydration desyncs Swiper's internal slide index from the DOM and drops a
  // slide (orphaned `<div class="">` in the wrapper). Mounting client-only makes
  // it behave like `gatsby develop`. A static fallback keeps the markup for SSR.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const paginationRef = useRef(null)

  // Memoize the slides so internal re-renders (e.g. hasNav) don't recreate the
  // SwiperSlide elements and let React reconcile against Swiper-mutated DOM.
  const slides = React.useMemo(
    () => React.Children.map(children, (child, i) => (
      <SwiperSlide key={i}>{child}</SwiperSlide>
    )),
    [children]
  )

  let sliderParams = {
    navigation: {
      prevEl: prevRef.current,
      nextEl: nextRef.current,
      disabledClass: 'is-disabled',
    },
    onBeforeInit: (swiper) => {
      swiper.params.navigation.prevEl = prevRef.current
      swiper.params.navigation.nextEl = nextRef.current
      if (hasPagination) {
        swiper.params.pagination.el = paginationRef.current
      }
    },
    ...options
  }

  if (hasPagination) {
    sliderParams = {
      ...sliderParams,
      pagination: {
        el: paginationRef.current,
        clickable: true
      }
    }
  }

  const checkIfHasNav = () => {
    const windowWidth = window.innerWidth
    let tempHasNav = true

    if (nbItems === 1) {
      tempHasNav = false
    }

    if (options.breakpoints) {
      Object.entries(options.breakpoints).forEach((item, key) => {
        if (windowWidth >= parseInt(item[0])) {
          if (nbItems <= parseInt(item[1].slidesPerView)) {
            tempHasNav = false
          } else {
            tempHasNav = true
          }
        }
      })
    }

    setHasNav(tempHasNav)
  }

  useEffect(() => {
    checkIfHasNav()
  })

  const handleResize = useCallback(() => {
    checkIfHasNav()
  /* eslint-disable-next-line */
  }, [])

  // resize event listener
  useEventListener('resize', handleResize)

  return (
    <>
      <div className={`slider-controls ${!hasNav ? 'is-disabled' : ''} ${navCentered ? 'slider-controls--centered' : ''}`}>
        <button ref={prevRef} className="slider-controls__button center slider-controls__button--left">
          <svg className="icon-night" width="32" height="32">
            <use xlinkHref={arrowSlider.url} />
          </svg>
        </button>
        <button ref={nextRef} className="slider-controls__button center slider-controls__button--right">
          <svg className="icon-night" width="32" height="32">
            <use xlinkHref={arrowSlider.url} />
          </svg>
        </button>
      </div>

      <div className={ hasCards ? 'slider-cards__inner' : className ? className : ''}>
        { mounted
          ? <Swiper {...sliderParams}>
              {slides}
            </Swiper>
          : <div className="swiper">
              <div className="swiper-wrapper">
                {slides}
              </div>
            </div>
        }

        { hasPagination && name !== 'medias' &&
          <div ref={paginationRef} className={`${paginationClass ? paginationClass : ''} swiper-pagination ${navCentered ? 'swiper-pagination--centered' : ''}`}></div>
        }
      </div>

      { hasPagination && name === 'medias' &&
        <div ref={paginationRef} className={`swiper-pagination ${navCentered ? 'swiper-pagination--centered' : ''}`}></div>
      }

      <div className="sr-only" aria-hidden="true">
        {children}
      </div>
    </>
  )
}

export default React.memo(Slider)